// ─────────────────────────────────────────────
//  caseBarSub — WHAT the Timeline case bar's SECOND line prints, and what it
//  gives up first when the bar is too narrow to print all of it.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  ⚠️ THE SUPERVISING MD IS THE PAYLOAD OF THIS LINE. `caseBarLabel` already
//  makes that argument for the provider's name on the line above; this file is
//  the same argument one line down, and it is a SAFETY argument rather than a
//  cosmetic one. The surgeon is context. The supervising anaesthesiologist is
//  the person a CRNA in that room escalates to.
//
//  ⚠️ AND UNTIL NOW THE MD WAS THE FIRST THING LOST. `timeline.buildTimeline`
//  joins the line as "procedure · surgeon · ⚕ MD" and the bar prints it as ONE
//  string under `text-overflow: ellipsis`, which clips from the RIGHT — so the
//  narrower the bar, the more certainly the *supervisor* is what the ellipsis
//  eats while the surgeon's name survives in full. Measured against the live
//  Wesley Chapel board of 2026-08-18 on a 1000px track, WC TEE
//  ("SENGUPTA · ⚕ …") and WOR 10 ("Deukmedjian + 1 · ⚕ …") both printed a
//  surgeon and no supervisor. Nobody had to break anything for that: it is what
//  the browser does to a string that is too long.
//
//  So the line DEGRADES on purpose, from the least clinically useful end:
//
//    L0  "On call · EGD · Deukmedjian + 1 · ⚕ Colin Wilson"   everything
//    L1  "On call · Deukmedjian + 1 · ⚕ Colin Wilson"         drop the procedure
//    L2  "On call · ⚕ Colin Wilson"                           drop the surgeon
//    L3  "⚕ Colin Wilson"                                     drop the on-call tag
//    L4  "⚕ C. Wilson"                                        first name → initial
//    L5  "⚕ Wilson"                                           surname alone
//    L6  "⚕ Wils…"                                            CSS ellipsis — the
//                                                             floor, not a rung
//
//  The on-call tag is given up at L3 and not sooner because it is the ONE part
//  of this line the bar also says another way (its own tint, and the room's
//  card) — but it is still a fact about the room, so it outranks nothing else.
//
//  ⚠️ NEVER INVENT A DOCTOR. The "⚕" is written ONLY when a name is present.
//  A case with no attending degrades along the surgeon instead and, when it has
//  neither, renders an EMPTY line — the bar then draws nothing rather than a
//  dagger with nobody after it. Showing a supervising MD who is not assigned is
//  worse than showing none; that rule is the reason this module returns text
//  and never a placeholder.
//
//  ⚠️ NOTHING DROPPED HERE IS LOST. `CaseBar`'s tooltip and `caseBarSpeech`
//  are built from the UNDEGRADED case row, so the hover text and the screen
//  reader still carry the procedure, the surgeon and the supervisor in full.
//
//  Pure: no React, no DOM measuring — jsdom has no layout, so a width measured
//  inside a component is a width that cannot be tested. The caller passes the
//  measured px in (`caseBarLabel.labelBudgetPx`) and everything here is
//  arithmetic, against the same per-character estimate the sub-line is drawn at.
// ─────────────────────────────────────────────

import { SUB_CHAR_PX, textPx, initialledName, surnameOf, stripRoleSuffix } from "./caseBarLabel";

// The separator `buildTimeline` joins this line with, and the mark that tells a
// reader at a glance which of two names is the supervisor rather than the
// surgeon. Both are duplicated NOWHERE else in this module.
export const SUB_SEP = " · ";
export const MD_MARK = "⚕";

// The tag a timeless on-call row carries at the head of its sub-line.
export const ON_CALL_TAG = "On call";

const clean = (v) => String(v ?? "").trim();

/** "Colin Wilson" → "⚕ Colin Wilson"; "" → "" (never a bare mark). */
const marked = (name) => (name ? `${MD_MARK} ${name}` : "");

/**
 * The three facts this line carries, read off a `buildTimeline` case row.
 *
 * Every case row already carries `procedure`, `surgeon`, `attending` and
 * `onCall` as separate fields — `sub` is the pre-joined convenience string, not
 * the source. Reading the FIELDS is what lets the line be re-joined at a
 * shorter length instead of chopped by the browser.
 */
export function subParts(c) {
  return {
    onCall: Boolean(c?.onCall),
    procedure: clean(c?.procedure),
    surgeon: clean(c?.surgeon),
    attending: clean(c?.attending),
  };
}

/** Join the non-empty pieces the way `buildTimeline` joins them. */
const join = (...pieces) => pieces.filter(Boolean).join(SUB_SEP);

/**
 * Every rung of the ladder for these parts, widest first.
 *
 * With an attending, identity is the MD and the rungs below L3 abbreviate them.
 * WITHOUT one there is no identity to protect, so the ladder stops at the
 * surgeon: a surgeon cell is already a short code or a surname ("TANDON",
 * "Kelesidis + 1") and initialling it would turn a name the board reads every
 * day into something nobody recognises.
 */
export function subLadder({ onCall = false, procedure = "", surgeon = "", attending = "" } = {}) {
  const tag = onCall ? ON_CALL_TAG : "";
  const p = clean(procedure);
  const s = clean(surgeon);
  const a = clean(attending);

  if (!a) {
    // No supervising MD on this row. Say nothing about one — not a mark, not a
    // placeholder, not the word "none". An empty last rung is a bar that draws
    // no second line at all, which is the honest answer.
    const noMd = [join(tag, p, s), join(tag, s), s];
    return noMd.filter((t, i, all) => i === 0 || t !== all[i - 1]);
  }

  const bare = stripRoleSuffix(a);
  const rungs = [
    join(tag, p, s, marked(a)),
    join(tag, s, marked(a)),
    join(tag, marked(a)),
    marked(a),
    marked(initialledName(bare)),
    marked(surnameOf(bare)),
  ];
  // Collapse rungs that came out identical (no procedure, no surgeon, no tag,
  // a single-token name), so `level` still means "how much was given up".
  return rungs.filter((t, i, all) => i === 0 || t !== all[i - 1]);
}

/**
 * Pick the widest rung of the ladder that fits.
 *
 * @param parts     { onCall, procedure, surgeon, attending }
 * @param budgetPx  usable px inside the bar, or null/0 when unmeasured
 * @param charPx    per-character estimate for the sub-line's type style
 * @returns { text, level, fits }
 *          `fits` false means even the last rung is wider than the bar and the
 *          browser's ellipsis will finish the job — on the MD's surname, which
 *          is the whole point.
 *
 * ⚠️ UNMEASURED MEANS "ASSUME THERE IS ROOM", the same reading `fitBarName` and
 * `CaseBar` take of a zero `trackWidth`. Degrading on a width nobody measured
 * would abbreviate every supervisor on the first paint of every board.
 */
export function fitBarSub(parts, budgetPx = null, charPx = SUB_CHAR_PX) {
  const ladder = subLadder(parts);
  const full = { text: ladder[0], level: 0, fits: true };
  if (!(budgetPx > 0)) return full;
  for (let i = 0; i < ladder.length; i += 1) {
    if (textPx(ladder[i], charPx) <= budgetPx) return { text: ladder[i], level: i, fits: true };
  }
  return { text: ladder[ladder.length - 1], level: ladder.length - 1, fits: false };
}

/** Convenience for a renderer that holds a case row and a measured budget. */
export function fitCaseSub(c, budgetPx = null, charPx = SUB_CHAR_PX) {
  return fitBarSub(subParts(c), budgetPx, charPx);
}
